export type GoogleUserData = {
  email?: string;
  first_name?: string;
  address?: {
    street?: string;
    postal_code?: string;
  };
};

export type GoogleConversionEventType =
  | "google_tag_container"
  | "lead_capture_conversion_label"
  | "purchase_conversion_label"
  | "trial_request_conversion_label";

export type GoogleEvent = { action: string; params: Record<string, string> };

let userData = getSessionUserData();

/**
 * Get `GoogleUserData` from `sessionStorage`.
 *
 * @returns
 */
function getSessionUserData(): GoogleUserData | false {
  let userData: GoogleUserData | false = false;

  try {
    const serializedUserData = sessionStorage.getItem("user-data");

    if (serializedUserData) {
      userData = JSON.parse(serializedUserData);

      if (userData) {
        setUserData(userData);
      }
    }
  } catch (error) {
    console.warn(`Error while retrieving user-data cookie.`, error);
  }

  return userData;
}

/**
 *
 * @param updatedUserData
 */
export function setUserData(data: GoogleUserData) {
  userData = data;
  if (window.gtag) {
    window.gtag("set", "user_data", userData);
  }
}

/**
 *
 */
export function storeUserData() {
  if (userData) {
    try {
      sessionStorage.setItem("user-data", JSON.stringify(userData));
    } catch (error) {
      console.warn(`Error while setting user-data cookie.`, error);
    }
  }
}

/**
 *
 */
export function consentCookies() {
  window.dataLayer.push({ event: "cookies_consented" });
  window.gtag?.("consent", "update", {
    ad_storage: "granted",
    ad_user_data: "granted",
    ad_personalization: "granted",
    analytics_storage: "granted",
  });
}

/**
 *
 * @param events
 * @param callback
 */
export async function sendGoogleEvents(
  events: GoogleEvent | GoogleEvent[],
  callback?: () => unknown
) {
  if (!Array.isArray(events)) {
    events = [events];
  }

  console.log("Sending google events", events);

  const promises = events.map((event) => {
    return new Promise<void>((resolve) => {
      if (!window.gtag) {
        resolve();
        return;
      }

      const timeout = setTimeout(resolve, 1000);

      window.gtag("event", event.action, {
        ...event.params,
        event_callback() {
          clearTimeout(timeout);
          resolve();
        },
      });
    });
  });

  await Promise.all(promises);

  callback?.();
}

/**
 *
 * @param eventName
 * @param eventCategory
 * @returns
 */
export function getAnalyticsGoogleEvent(
  eventName: string,
  eventCategory?: string
): GoogleEvent {
  return {
    action: eventName,
    params: eventCategory ? {
      event_category: eventCategory,
    } : {},
  };
}

/**
 *
 * @param type
 * @param params
 * @returns
 * @throws if type is invalid.
 */
export function getConversionGoogleEvent(
  type: "trial" | "lead" | "purchase",
  params = {}
): GoogleEvent {
  let setting: GoogleConversionEventType;

  if (type === "trial") {
    setting = "trial_request_conversion_label";
  } else if (type === "lead") {
    setting = "lead_capture_conversion_label";
  } else if (type === "purchase") {
    setting = "purchase_conversion_label";
  } else {
    throw `Conversion type ${type} is invalid`;
  }

  return {
    action: "conversion",
    params: {
      send_to: `${window.ConversionSettings.google_tag_container}/${window.ConversionSettings[setting]}`,
      ...params,
    },
  };
}

window.ConversionTesting = {
  getConversionGoogleEvent,
  sendGoogleEvents,
  storeUserData,
  setUserData,
};
